import * as React from 'react'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { styled } from '@mui/material/styles'
import { Grid, Box, Button, IconButton, Stack } from '@mui/material'
import { Typography as MIUTypography } from '@mui/material'
import { InputAdornment } from '@mui/material'
import { createUser } from '../../services'
import { schema } from '../../utils/schemaValidation'
import { getMeasuringGroup } from '../../services'
import { Lock, AccountCircle, Visibility, VisibilityOff } from '@mui/icons-material'
import AlertCustom from '../../components/Alerts'
import Textfield from '../../components/FormsUI/Textfield'
import _ from 'lodash'

const BUTTON_COLOR = 'var(--color-green-light)'
const TEXT_BUTTON_COLOR = '#EDEEFF'

const Typography = styled(MIUTypography)(props => ({
  fontFamily: 'var(--font-family)',
  fontStyle: 'normal',
  fontSize: '1.125em',
  fontWeight: props.istitle ? 700 : 450,
  lineHeight: props.istitle ? '24px' : '24px',
  color: props.istitle ? '#1B1C37' : '#6A6A6A',
  textAlign: 'center',
}))

const ButtonCustom = styled(Button)({
  height: '40px',
  backgroundColor: BUTTON_COLOR,
  textTransform: 'capitalize',
  fontFamily: 'Avenir Next',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '22px',
  color: TEXT_BUTTON_COLOR,
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#0fa73d',
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: BUTTON_COLOR,
    borderColor: BUTTON_COLOR,
  },
})

const styleStack = {
  justifyContent: 'center',
  direction: 'column',
  alignItems: 'center',
  py: 2,
  px: '8vw',
  height: '100%',
  spacing: 2,
  width: '100%',
}

const boxStyle = {
  minWidth: '300px',
  maxWidth: '500px',
  bgcolor: 'background.paper',
  boxShadow: 14,
  pb: '50px',
  px: '25px',
  pt: 6,
  textAlign: 'center',
  borderRadius: '14px',
}

const INITIAL_FORM_STATE = {
  username: '',
  lastname: '',
  email: '',
  password_alt: '',
  password_confirmation_alt: '',
  measuring_group: '-1',
}

const FORM_VALIDATION = Yup.object().shape({
  ...schema.USERNAME,
  ...schema.LASTNAME,
  ...schema.EMAIL,
  ...schema.PASSWORD_ALT,
  ...schema.PASSWORD_CONFIRMATION_ALT,
  ...schema.MEASURING_GROUP,
})

const UsersRegistration = (props, ref) => {
  const alertRef = React.useRef()

  const [showPassword, setShowPassword] = React.useState(false)
  const [options, setOptions] = React.useState(null)
  const [isStored, setIsStored] = React.useState(null)

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = event => event.preventDefault()

  React.useImperativeHandle(ref, () => ({ isStored }))

  const handleClickLogin = async ({
    username,
    lastname,
    email,
    password_alt,
    measuring_group,
  }) => {
    let resultSave = { stored: true }
    const data = {
      nombre: username,
      apellido: lastname,
      email,
      contrasena: password_alt,
      idGrupoMedicion:
        measuring_group && measuring_group !== '-1'
          ? parseInt(measuring_group, 10)
          : null,
    }
    const response = await createUser(data)
    if (response?.typeaction !== 'created') {
      resultSave = { stored: false, message: response?.message }
    }
    return resultSave
  }

  React.useEffect(() => {
    if (options) return
    getMeasuringGroup()
      .then(workGroup => {
        let objOption = { '-1': 'No asignado' }
        if (workGroup?.res) {
          _.forEach(workGroup.res, res => {
            if (!Boolean(res.habilitado.data[0])) return
            let objNew = {}
            objNew[res.id_gm] = res.nombre
            objOption = { ...objOption, ...objNew }
          })
        }
        setOptions(objOption)
      })
      .catch(err => console.log('Error :>> ', err.message))
  }, [options])

  return (
    <Box>
      <Stack {...styleStack}>
        {!Boolean(isStored?.stored) && (
          <Box sx={boxStyle}>
            <Formik
              initialValues={{ ...INITIAL_FORM_STATE }}
              validationSchema={FORM_VALIDATION}
              onSubmit={async values => {
                const itsStored = await handleClickLogin(values)
                setIsStored(itsStored)
                alertRef.current?.openAlert(true)
              }}>
              <Form>
                <Grid container spacing={2} sx={{ pt: 0, width: '100%', ml: 0 }}>
                  <Grid item xs={6} style={{ paddingLeft: 0, paddingRight: 4 }}>
                    <Textfield
                      name="username"
                      label="Nombre"
                      placeholder="Ingrese su nombre"
                    />
                  </Grid>
                  <Grid item xs={6} style={{ paddingLeft: 4 }}>
                    <Textfield
                      name="lastname"
                      label="Apellido"
                      placeholder="Ingrese su apellido"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="email"
                      label="Ingrese su correo electrónico"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="user@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_alt"
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Nueva contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_confirmation_alt"
                      label="Confirmar contraseña"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Vuelve a ingresar la contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 4 }}>
                    <ButtonCustom variant="contained" fullWidth type="submit">
                      Registrarse
                    </ButtonCustom>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
          </Box>
        )}
      </Stack>
      {isStored?.stored && (
        <Box pt={1}>
          <Typography sx={{ color: '#000', fontWeight: 500 }}>
            Registro enviado para su aprobación.{<br />} De ser necesario comuníquese con
            el administrador para su identificación y pronta aprobación de la cuenta.
          </Typography>
        </Box>
      )}
      <AlertCustom
        ref={alertRef}
        severity={!Boolean(isStored?.stored) ? 'warning' : 'info'}>
        {!Boolean(isStored?.stored)
          ? isStored?.message
          : 'Registro enviado - Revisión pendiente.'}
      </AlertCustom>
    </Box>
  )
}

export default React.forwardRef(UsersRegistration)
